import {type IntlShape} from "react-intl";
import type {Money} from "@/api/apiClient";

/**
 * Formats a price range or single price with currency
 * @param min - Minimum price
 * @param max - Maximum price
 * @param intl - React Intl instance for formatting
 * @returns Formatted price string (e.g., "100 Kč" or "100–150 Kč")
 * @example
 * getPrice({amount: 100, ccy: "CZK"}, {amount: 100, ccy: "CZK"}, intl) // "100 Kč"
 * getPrice({amount: 100, ccy: "CZK"}, {amount: 150, ccy: "CZK"}, intl) // "100–150 Kč"
 */
export function getPrice(min: Money, max: Money, intl: IntlShape): string {
    if (min.ccy !== max.ccy) {
        console.warn("Currency mismatch in price range:", {min: min.ccy, max: max.ccy});
    }
    if (max.amount === min.amount) {
        return formatMoney(max, intl);
    }
    return `${min.amount}–${formatMoney(max, intl)}`;
}

/**
 * Formats money value with currency symbol
 * @param money - Money object with amount and currency code
 * @param intl - React Intl instance for currency formatting
 * @returns Formatted money string with non-breaking space (e.g., "100 Kč")
 * @example
 * formatMoney({amount: 100, ccy: "CZK"}, intl) // "100 Kč"
 * formatMoney({amount: 50, ccy: "EUR"}, intl) // "50 €"
 */
export function formatMoney(money: Money, intl: IntlShape): string {
    return `${money.amount}\u00A0${intl.formatMessage({id: money.ccy})}`;
}
